'use client';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';

import { GamesViewSkeleton } from './GamesViewSkeleton';
import { SportsFuturesFeedSkeleton } from './SportsFuturesFeedSkeleton';
import { TradingSidebarSkeleton } from './TradingSidebarSkeleton';

interface SportsPageSkeletonProps {
  sportSlug?: string;
}

export const SportsPageSkeleton = ({ sportSlug }: SportsPageSkeletonProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isLive = sportSlug === 'live';

  // Mirrors Sports.tsx: futures renders SportsFuturesFeed alone, with no
  // SportHeader/tabs/sidebar chrome around it.
  if (sportSlug === 'futures') {
    return (
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <SportsFuturesFeedSkeleton />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {/* Header Skeleton replicating SportHeader */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Logo, Title, and Search (live) / Toggle (non-live) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {!isLive && (
                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
              )}
              <Skeleton variant="text" width={120} height={32} />
            </Box>
            {isLive ? (
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: '10px' }} />
            ) : (
              <Skeleton variant="rounded" width={140} height={24} sx={{ borderRadius: 12 }} />
            )}
          </Box>

          {/* Games / Props Tabs and Search — hidden for Live, matching SportHeader */}
          {!isLive && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
              </Box>
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
          )}
        </Box>

        <GamesViewSkeleton />
      </Box>

      {/* Right Sidebar Skeleton - Desktop Only */}
      {isDesktop && (
        <Box
          sx={{
            width: { lg: 320, xl: 360 },
            flexShrink: 0,
          }}
        >
          <TradingSidebarSkeleton />
        </Box>
      )}
    </>
  );
};
